/**
 * templateReferences — which `$(name)` a response text references (R20 chat 3, C7)
 *
 * A PORT of `pratbot/core/templating.py::references()` and its tokenizer
 * (`_parse_call`, `_bare_names`), so the command editor can say "this will be
 * printed as typed" while the author is still typing. Unknown names are left
 * literal at render by design (R20 N7); this is where a typo gets caught.
 *
 * It must agree with the backend exactly, or the hint lies. Rules carried over:
 *   - a name is one or more of TEMPLATE_NAME_CHARS, read greedily after `$(`;
 *   - whitespace may precede `)` or an argument, so `$(song )` IS a reference;
 *   - arguments are single-quoted, with \' and \\ escapes;
 *   - a name WITH arguments is not a reference (it renders as typed), except
 *     `random.pick`, whose every option is scanned for bare references;
 *   - a nested random.pick inside an option is not scanned (it is not evaluated);
 *   - a malformed call is skipped two characters on, and scanning continues.
 *
 * TEMPLATE_NAME_CHARS must equal `_NAME_CHARS` in core/templating.py (and
 * core/variables.py). `python -m scripts.r20_exercise manifest` compares them.
 */

/** Must equal core/templating.py `_NAME_CHARS`, character for character. */
export const TEMPLATE_NAME_CHARS =
  'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789._'

const PREFIX = '$('
const WS = ' \t\r\n'
const RANDOM_PICK = 'random.pick'

type Call = { name: string; args: string[]; end: number }

/** Parse `$(name 'arg' ...)` starting at the `$`. Null when not well-formed. */
function parseCall(text: string, start: number): Call | null {
  const n = text.length
  let k = start + PREFIX.length
  const nameStart = k
  while (k < n && TEMPLATE_NAME_CHARS.includes(text[k])) k++
  const name = text.slice(nameStart, k)
  if (!name) return null

  const args: string[] = []
  while (k < n) {
    while (k < n && WS.includes(text[k])) k++
    if (k >= n) return null
    const ch = text[k]
    if (ch === ')') return { name, args, end: k + 1 }
    if (ch !== "'") return null
    k++
    let buf = ''
    let closed = false
    while (k < n) {
      const c = text[k]
      if (c === '\\' && k + 1 < n && "'\\".includes(text[k + 1])) {
        buf += text[k + 1]
        k += 2
        continue
      }
      if (c === "'") {
        k++
        closed = true
        break
      }
      buf += c
      k++
    }
    if (!closed) return null
    args.push(buf)
  }
  return null
}

/** Bare references in one random.pick option. Nested calls are not scanned. */
function bareNames(text: string): Set<string> {
  const found = new Set<string>()
  let i = 0
  while (i < text.length) {
    const j = text.indexOf(PREFIX, i)
    if (j === -1) break
    const parsed = parseCall(text, j)
    if (parsed === null) {
      i = j + PREFIX.length
      continue
    }
    if (parsed.args.length === 0 && parsed.name !== RANDOM_PICK) found.add(parsed.name)
    i = parsed.end
  }
  return found
}

/** Every bare `$(name)` in `text`, including inside every random.pick option. */
export function templateReferences(text: string): Set<string> {
  const found = new Set<string>()
  if (!text || !text.includes(PREFIX)) return found
  let i = 0
  while (i < text.length) {
    const j = text.indexOf(PREFIX, i)
    if (j === -1) break
    const parsed = parseCall(text, j)
    if (parsed === null) {
      i = j + PREFIX.length
      continue
    }
    if (parsed.name === RANDOM_PICK) {
      for (const option of parsed.args) bareNames(option).forEach((x) => found.add(x))
    } else if (parsed.args.length === 0) {
      found.add(parsed.name)
    }
    i = parsed.end
  }
  return found
}
